import React from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { createDefaultTier, generateTierName, ROLE_LABELS } from "../policyBuilderState";

const TierCard = ({ tier, isSelected, onSelect, onDuplicate, onDelete, canDelete, disabled, hasError, tierStatus, currency = "EUR" }) => {
  const displayName = tier.name || generateTierName(tier, currency);
  const chainLabels = tier.chain.map((s) => s.label || (s.role ? ROLE_LABELS[s.role] : "User"));
  const chainSummary = chainLabels.length > 0 ? chainLabels.join(" → ") : "No steps";
  const hasTierIssue = tierStatus === "warn";
  const showMarker = tierStatus != null;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-current={isSelected ? "true" : undefined}
      aria-label={hasTierIssue ? "Tier has issues" : "Tier valid"}
      className={`border rounded-card p-3 cursor-pointer transition-colors text-left ${
        isSelected ? "border-primary bg-primary-soft" : "border-border bg-card hover:border-text-3"
      } ${hasError ? "border-danger" : ""}`}
      onClick={() => onSelect(tier.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(tier.id);
        }
      }}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="min-w-0 flex-1">
            <div className="font-medium text-sm text-text truncate">{displayName}</div>
            <div className="text-xs text-text-2 mt-1 truncate" title={chainSummary}>
              {chainSummary}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-2">
          {showMarker && (hasTierIssue ? (
            <span className="text-warning text-xs" aria-label="Tier has issues">
              &#x26A0;
            </span>
          ) : (
            <span className="text-success text-xs" aria-label="Tier valid">
              &#x2713;
            </span>
          ))}
          {!disabled && (
            <div className="flex gap-1 shrink-0">
              <button
                onClick={(e) => { e.stopPropagation(); onDuplicate(tier.id); }}
                className="p-1 text-text-2 hover:text-text rounded-ctrl"
                title="Duplicate tier"
              >
                <FontAwesomeIcon icon={icon({ name: "copy", style: "regular" })} className="text-xs" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(tier.id); }}
                className={`p-1 rounded-ctrl ${canDelete ? "text-danger hover:text-danger" : "text-text-3 cursor-not-allowed"}`}
                disabled={!canDelete}
                title={canDelete ? "Delete tier" : "Cannot delete last tier"}
              >
                <FontAwesomeIcon icon={icon({ name: "trash", style: "regular" })} className="text-xs" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PolicyHealthStrip = ({ status, issueCount, topIssueSummary }) => {
  if (status === "ok") {
    return (
      <div className="flex items-center justify-between gap-2 py-2 px-3 rounded-card bg-success-soft text-success text-sm">
        <span className="flex items-center gap-2 font-medium">
          <FontAwesomeIcon icon={icon({ name: "circle-check", style: "solid" })} className="text-sm" />
          Compliant
        </span>
        <span className="text-xs opacity-90">All tiers valid</span>
      </div>
    );
  }
  return (
    <div
      className="flex items-center justify-between gap-2 py-2 px-3 rounded-card bg-warning-soft border border-warning text-sm"
      role="status"
      aria-label={topIssueSummary ? `Needs attention: ${topIssueSummary}` : `${issueCount} issue${issueCount !== 1 ? "s" : ""}`}
    >
      <span className="font-medium text-warning">Needs attention</span>
      <span className="text-warning text-xs">{issueCount} issue{issueCount !== 1 ? "s" : ""}</span>
    </div>
  );
};

const TierListPanel = ({ tiers, selectedTierId, onSelectTier, onTiersChange, onAddTier, currency = "EUR", disabled, errors, policyHealth }) => {
  const tierErrors = new Set(errors.filter((e) => e.tierId).map((e) => e.tierId));

  const handleDuplicate = (tierId) => {
    const source = tiers.find((t) => t.id === tierId);
    if (!source) return;
    const dup = { ...structuredClone(source), id: createDefaultTier().id };
    dup.name = source.name ? `${source.name} (copy)` : "";
    const idx = tiers.findIndex((t) => t.id === tierId);
    const newTiers = [...tiers];
    newTiers.splice(idx + 1, 0, dup);
    onTiersChange(newTiers);
    onSelectTier(dup.id);
  };

  const handleDelete = (tierId) => {
    if (tiers.length <= 1) return;
    const newTiers = tiers.filter((t) => t.id !== tierId);
    onTiersChange(newTiers);
  };

  return (
    <div className="bg-card border border-card-bd rounded-card p-4 min-w-0 flex flex-col">
      <div className="flex items-center justify-between pb-3 border-b border-border">
        <h3 className="text-sm font-semibold text-text">Tiers</h3>
        {!disabled && (
          <button
            type="button"
            onClick={onAddTier}
            className="text-primary hover:text-primary-hover text-sm font-medium cursor-pointer"
          >
            + Add tier
          </button>
        )}
      </div>

      {policyHealth && tiers.length > 0 && (
        <div className="pt-3">
          <PolicyHealthStrip
            status={policyHealth.status}
            issueCount={policyHealth.issueCount}
            topIssueSummary={policyHealth.topIssueSummary}
          />
        </div>
      )}

      <div className="space-y-2 pt-3">
        {tiers.map((tier) => (
          <TierCard
                key={tier.id}
                tier={tier}
                isSelected={tier.id === selectedTierId}
                onSelect={onSelectTier}
                onDuplicate={handleDuplicate}
                onDelete={handleDelete}
                canDelete={tiers.length > 1}
                disabled={disabled}
                hasError={tierErrors.has(tier.id)}
                tierStatus={policyHealth?.perTierStatusById?.[tier.id]}
                currency={currency}
          />
        ))}
        {tiers.length === 0 && !disabled && (
              <button
                type="button"
                onClick={onAddTier}
                className="w-full py-3 border border-dashed border-border rounded-card text-text-2 text-sm hover:border-text-3 hover:text-text-2 transition-colors"
              >
            + Add tier
          </button>
        )}
      </div>
    </div>
  );
};

TierListPanel.propTypes = {
  tiers: PropTypes.array.isRequired,
  selectedTierId: PropTypes.string,
  onSelectTier: PropTypes.func.isRequired,
  onTiersChange: PropTypes.func.isRequired,
  onAddTier: PropTypes.func.isRequired,
  currency: PropTypes.string,
  disabled: PropTypes.bool,
  errors: PropTypes.array,
  policyHealth: PropTypes.shape({
    status: PropTypes.oneOf(["ok", "warn"]),
    issueCount: PropTypes.number,
    topIssueSummary: PropTypes.string,
    perTierStatusById: PropTypes.object,
  }),
};

export default TierListPanel;
